import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { and, eq, sql } from "drizzle-orm";
import { GameData, select } from "gamedata/GameData";
import { redirect } from "react-router";
import invariant from "tiny-invariant";
import { z } from "zod";
import { requireSess } from "~/auth.server";
import { StatsPage } from "~/pages/tracker/StatsPage";
import { pg } from "~/pg.server";
import { Realm, tt } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { logPageView } from "~/util/stats";

dayjs.extend(utc);
dayjs.extend(timezone);

const PageQuery = z.object({
  date: z.optional(z.string()),
  period: z.optional(z.string()),
});

type Item = {
  id: number;
  itemName: string;
  type: string;
  name: string;
  icon: string;
};

type Loot = {
  date: Date;
  count: number;
  item: Item | null;
  type: "ITEM" | "EXP" | "SP";
};

const TZ = "Asia/Yekaterinburg";

$PAGE({
  path: "tracker/:characterId/stats",
  loader: $LOADER?.(async (args) => {
    // Log
    logPageView(args);
    //
    const sess = requireSess(args.req.sess);

    const query = PageQuery.parse(
      Object.fromEntries(args.url.searchParams.entries()),
    );

    const Params = z.object({ characterId: z.string() });
    const params = Params.parse(args.params);

    const characterResult = await pg.db
      .select({
        id: pg.tkChars.id,
        name: pg.tkChars.charName,
        timezone: pg.users.timezone,
      })
      .from(pg.tkChars)
      .leftJoin(pg.users, eq(pg.tkChars.userId, pg.users.id))
      .where(
        and(
          eq(pg.tkChars.id, parseInt(params.characterId)),
          eq(pg.tkChars.userId, sess.user.id),
        ),
      );

    const character = characterResult.at(0);
    if (!character) {
      return redirect("/tracker");
    }

    const timezone = character.timezone ?? "UTC";
    const { startDate, endDate } = getPeriod({ period: query.period, tz: TZ });

    const diff = Math.round(endDate.diff(startDate, "days", true));

    const activeDays = await pg.db
      .select({
        value: sql<
          string[]
        >`ARRAY_AGG(DISTINCT DATE_TRUNC('day', ${pg.tkLoot.timestamp} AT TIME ZONE ${timezone})::text)`,
      })
      .from(pg.tkLoot)
      .where(eq(pg.tkLoot.characterId, character.id))
      .limit(100);

    const today = sql`
      (${
        pg.tkLoot.timestamp
      } AT TIME ZONE ${timezone})::date >= ${startDate.toDate().toISOString()}
      AND
      (${
        pg.tkLoot.timestamp
      } AT TIME ZONE ${timezone})::date < ${endDate.toDate().toISOString()}`;

    const field = diff === 1 ? sql`'hour'` : sql`'day'`;

    const loot = await pg.db
      .select({
        date: sql`DATE_TRUNC(${field}, ${pg.tkLoot.timestamp} AT TIME ZONE 'UTC')`.mapWith(
          Date,
        ),
        count: sql`SUM(${pg.tkLoot.count})`.mapWith(Number),
        itemId: sql`${pg.tkLoot.itemId}`.mapWith(Number),
      })
      .from(pg.tkLoot)
      .where(and(today, eq(pg.tkLoot.characterId, character.id)))
      .groupBy((t) => [t.itemId, t.date]);

    return {
      authUser: args.req.sess?.user,
      timezone,
      character,
      activeDays:
        activeDays.at(0)?.value.map((ad) => dayjs(ad).format("YYYY-MM-DD")) ??
        [],
      loot: loot.reduce<Loot[]>((prevValue, data) => {
        prevValue.push({
          count: data.count,
          date: dayjs(data.date).tz(timezone).toDate(),
          item:
            data.itemId > 0
              ? selectItemData(args.gd, args.realm, data.itemId)
              : null,
          type: data.itemId === -1 ? "EXP" : data.itemId === -2 ? "SP" : "ITEM",
        });
        return prevValue;
      }, []),
    };
  }),
  meta: (args) => {
    const title = "Tracker";
    const characterName = args.data.character.name;
    return {
      title: `${characterName} - ${title} - L2Hub`,
      description: "Stats character L2Hub",
    };
  },
  render: ({ data }) => {
    return <StatsPage data={data} />;
  },
});

function selectItemData(gd: GameData, realm: Realm, itemId: number) {
  const item = gd.itemsById.get(itemId);
  invariant(item);

  const t = tt.bind(null, realm.lang);

  return select(item, {
    id: true,
    itemName: true,
    name: t,
    icon: true,
    type: true,
  });
}

function getPeriod(params: { period?: string; tz: string }) {
  const todayDate = dayjs().tz(params.tz);
  const yesterdayDate = todayDate.subtract(1, "day");
  const lastWeek = todayDate.subtract(6, "day");
  const lastMonth = todayDate.subtract(29, "day");

  let period: string | undefined;
  switch (params.period) {
    case "today":
      period = `${todayDate.startOf("day").format("YYYY-MM-DD")}:${todayDate
        .endOf("day")
        .format("YYYY-MM-DD")}`;
      break;
    case "yesterday":
      period = `${yesterdayDate
        .startOf("day")
        .format("YYYY-MM-DD")}:${yesterdayDate
        .endOf("day")
        .format("YYYY-MM-DD")}`;
      break;
    case "week":
      period = `${lastWeek.startOf("day").format("YYYY-MM-DD")}:${todayDate
        .endOf("day")
        .format("YYYY-MM-DD")}`;
      break;
    case "month":
      period = `${lastMonth.startOf("day").format("YYYY-MM-DD")}:${todayDate
        .endOf("day")
        .format("YYYY-MM-DD")}`;
      break;
    default:
      period = params.period;
      break;
  }

  const [strStartDate, strEndDate] = period ? period.split(":", 2) : [];

  const startDate = dayjs(strStartDate).isValid()
    ? dayjs(strStartDate).tz(params.tz).startOf("day")
    : dayjs().tz(params.tz).startOf("day");
  const endDate = dayjs(strEndDate).isValid()
    ? dayjs(strEndDate).tz(params.tz).endOf("day")
    : dayjs().tz(params.tz).endOf("day");

  return {
    startDate,
    endDate,
  };
}
